import { Controller } from "@hotwired/stimulus";

// Non-null/undefined guard — needed because a valid item_count can be 0, so a
// plain truthiness check would wrongly skip it.
const present = (value) => value !== undefined && value !== null;

// Optimistic cart quantity controls for the side drawer.
//
// The quantity number updates instantly on click; subtotals are authoritative
// from the server (they can include price adjustments / quantity promotions, so
// the client never computes them). Each mutation persists in the background
// (debounced) and reconciles the row + cart subtotals from the JSON response.
// Out-of-order responses are ignored via per-row + global generations so a slow
// earlier request can't overwrite a newer one.
export default class extends Controller {
  static values = { persistDelay: { type: Number, default: 350 } };

  connect() {
    this.timers = new Map(); // itemId -> { timer, qty, row } (debounce)
    this.rowGen = new Map(); // itemId -> latest issued request generation
    this.footerSeq = 0; // monotonic issue counter across all requests
    this.footerApplied = 0; // highest seq whose footer/badge was applied
    this.onHide = () => this.flush();
    this.offcanvas = this.element.closest(".offcanvas");
    // Persist any pending change if the drawer is closed before the debounce fires.
    this.offcanvas?.addEventListener("hide.bs.offcanvas", this.onHide);
  }

  disconnect() {
    this.flush();
    this.offcanvas?.removeEventListener("hide.bs.offcanvas", this.onHide);
  }

  increment(event) {
    this.changeQty(event, 1);
  }

  decrement(event) {
    this.changeQty(event, -1);
  }

  changeQty(event, delta) {
    const row = event.currentTarget.closest("[data-cart-item-id]");
    if (!row) return;

    const min = Number(row.dataset.minQty || 1);
    const qtyEl = row.querySelector("[data-cart-qty]");
    const current = Number(qtyEl.textContent.trim()) || min;
    const next = Math.max(min, current + delta);
    if (next === current) return;

    // Optimistic: quantity only. The subtotal stays as the server rendered it
    // until the persist response reconciles it.
    qtyEl.textContent = next;
    this.setDecrementDisabled(row, next <= min);
    this.schedulePersist(row, next);
  }

  remove(event) {
    const row = event.currentTarget.closest("[data-cart-item-id]");
    if (!row) return;

    const id = row.dataset.cartItemId;
    this.cancelPersist(id);
    // Bump the row generation so any in-flight update response for this row is
    // ignored once it's gone.
    this.rowGen.set(id, (this.rowGen.get(id) || 0) + 1);
    row.remove();
    this.adjustBadges(-1);
    this.setHeaderCount(this.rows.length);
    if (this.rows.length === 0) this.showEmptyState();
    this.persistDelete(id);
  }

  // --- DOM helpers ---

  get rows() {
    return Array.from(this.element.querySelectorAll("[data-cart-item-id]"));
  }

  setDecrementDisabled(row, disabled) {
    const btn = row.querySelector('[data-action*="cart-quantity#decrement"]');
    if (btn) btn.disabled = disabled;
  }

  showEmptyState() {
    this.element.querySelector("[data-cart-empty]")?.classList.remove("d-none");
    this.element.querySelector("[data-cart-filled]")?.classList.add("d-none");
    // Footer (subtotal + CTAs) lives outside the body; clear it once empty.
    (this.offcanvas || document).querySelector("#cart-panel-footer")?.replaceChildren();
  }

  // The badge is rendered for both the desktop and mobile triggers, so update
  // every instance rather than a single id.
  get badges() {
    return Array.from(document.querySelectorAll(".js-cart-badge"));
  }

  adjustBadges(delta) {
    const first = this.badges[0];
    if (!first) return;
    this.setBadges(Math.max(0, (Number(first.textContent.trim()) || 0) + delta));
  }

  setBadges(count) {
    this.badges.forEach((badge) => {
      badge.textContent = count > 0 ? String(count) : "";
      badge.classList.toggle("d-none", count === 0);
    });
  }

  // `cart:count` window event from pages that change the cart without reloading the nav.
  syncCount({ detail: { count } }) {
    this.setBadges(count);
    this.setHeaderCount(count);
  }

  // Muted "· N items" suffix in the panel header (a sibling of #cart-panel-body).
  setHeaderCount(count) {
    const el = (this.offcanvas || document).querySelector("[data-cart-header-count]");
    if (!el) return;
    el.textContent = `· ${count} item${count === 1 ? "" : "s"}`;
    el.classList.toggle("d-none", count === 0);
  }

  // Apply a server cart subtotal only if no newer request's response has already
  // landed (last-issued wins, not last-responded).
  setFooter(text, seq) {
    if (!present(text) || seq < this.footerApplied) return;
    this.footerApplied = seq;
    const footer = (this.offcanvas || document).querySelector("#cart-panel-subtotal strong");
    if (footer) footer.textContent = text;
  }

  // --- persistence (background, debounced) ---

  schedulePersist(row, qty) {
    const id = row.dataset.cartItemId;
    this.cancelPersist(id);
    const timer = setTimeout(() => {
      this.timers.delete(id);
      this.persist(id, qty, row);
    }, this.persistDelayValue);
    this.timers.set(id, { timer, qty, row });
  }

  cancelPersist(id) {
    const pending = this.timers.get(id);
    if (pending) {
      clearTimeout(pending.timer);
      this.timers.delete(id);
    }
  }

  flush() {
    this.timers.forEach(({ timer, qty, row }, id) => {
      clearTimeout(timer);
      this.persist(id, qty, row);
    });
    this.timers.clear();
  }

  async persist(id, qty, row) {
    const rowSeq = (this.rowGen.get(id) || 0) + 1;
    this.rowGen.set(id, rowSeq);
    const seq = ++this.footerSeq;
    try {
      const res = await fetch(`/shopping_cart_item/${id}.json`, {
        method: "PATCH",
        headers: this.jsonHeaders,
        body: JSON.stringify({ quantity: qty }),
      });
      if (!res.ok) return;
      const data = await res.json();
      // Row subtotal: only if this is still the latest request for the row and
      // the row wasn't removed meanwhile.
      if (this.rowGen.get(id) === rowSeq && row.isConnected && present(data.item_subtotal)) {
        const el = row.querySelector("[data-cart-subtotal]");
        if (el) el.textContent = data.item_subtotal;
      }
      this.setFooter(data.cart_subtotal, seq);
    } catch {
      // Leave the last server-rendered subtotal in place; the cart page reconciles.
    }
  }

  async persistDelete(id) {
    const seq = ++this.footerSeq;
    try {
      const res = await fetch(`/shopping_cart_item/${id}.json`, {
        method: "DELETE",
        headers: this.jsonHeaders,
      });
      if (!res.ok) return;
      const data = await res.json();
      this.setFooter(data.cart_subtotal, seq);
      if (present(data.item_count)) {
        this.setBadges(data.item_count);
        this.setHeaderCount(data.item_count);
      }
    } catch {
      // Keep the optimistic removal.
    }
  }

  get jsonHeaders() {
    return {
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }
}
